import { ArrowLeft, Edit2, Image, Palette, Plus, Ruler, Settings, Trash2, Type, icons } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

// Custom variables icon - hexagon with centered circle
const VariablesIcon = ({ size = 16, className = "" }: { size?: number; className?: string }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <title>Variables Icon</title>
    {/* Hexagon outline */}
    <polygon
      points="12,2 20,6 20,18 12,22 4,18 4,6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    />
    {/* Centered circle */}
    <circle cx="12" cy="12" r="4" fill="currentColor" />
  </svg>
);

import { HexColorPicker } from "react-colorful";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  type ColorVariable,
  type FontFamilyVariable,
  type IconVariable,
  type SizeVariable,
  useVariablesStore,
} from "@/store/variablesStore";
import { loadCraftspaceVariables } from "@/services/variablesService";
import { isElectron } from "@/services/storage/LocalFileSystemClient";

interface CraftspaceVariablesProps {
  craftspaceId?: string;
  embedded?: boolean;
  section?: "colors" | "sizes" | "fonts" | "icons";
}

const CraftspaceVariables = ({ craftspaceId: propCraftspaceId, embedded = false, section }: CraftspaceVariablesProps = {}) => {
  const params = useParams<{ craftspaceId: string }>();
  const craftspaceId = propCraftspaceId ?? params.craftspaceId;
  const navigate = useNavigate();
  const {
    getCraftspaceVariables,
    setCraftspaceVariables,
    addCraftspaceColorVariable,
    updateCraftspaceColorVariable,
    deleteCraftspaceColorVariable,
    addCraftspaceSizeVariable,
    updateCraftspaceSizeVariable,
    deleteCraftspaceSizeVariable,
    addCraftspaceFontFamilyVariable,
    updateCraftspaceFontFamilyVariable,
    deleteCraftspaceFontFamilyVariable,
    addCraftspaceIconVariable,
    updateCraftspaceIconVariable,
    deleteCraftspaceIconVariable,
  } = useVariablesStore();

  const variables = craftspaceId
    ? getCraftspaceVariables(craftspaceId)
    : { colors: [], sizes: [], fontFamilies: [], icons: [] };

  useEffect(() => {
    if (!craftspaceId || !isElectron()) return;
    const load = async () => {
      const wsVars = await loadCraftspaceVariables(craftspaceId);
      setCraftspaceVariables(craftspaceId, wsVars);
    };
    void load();
  }, [craftspaceId, setCraftspaceVariables]);


  // Dialog states
  const [colorDialogOpen, setColorDialogOpen] = useState(false);
  const [sizeDialogOpen, setSizeDialogOpen] = useState(false);
  const [fontDialogOpen, setFontDialogOpen] = useState(false);
  const [iconDialogOpen, setIconDialogOpen] = useState(false);

  // Form states
  const [newColorName, setNewColorName] = useState("");
  const [newColorValue, setNewColorValue] = useState("#3B82F6");
  const [editingColor, setEditingColor] = useState<ColorVariable | null>(null);

  const [newSizeName, setNewSizeName] = useState("");
  const [newSizeValue, setNewSizeValue] = useState("");
  const [newSizeUnit, setNewSizeUnit] = useState("px");
  const [editingSize, setEditingSize] = useState<SizeVariable | null>(null);

  const [newFontName, setNewFontName] = useState("");
  const [newFontValue, setNewFontValue] = useState("");
  const [editingFont, setEditingFont] = useState<FontFamilyVariable | null>(null);

  const [newIconName, setNewIconName] = useState("");
  const [newIconValue, setNewIconValue] = useState("");
  const [iconNameTouched, setIconNameTouched] = useState(false);
  const [editingIcon, setEditingIcon] = useState<IconVariable | null>(null);
  const [iconSearch, setIconSearch] = useState("");

  const resetColorForm = () => {
    setNewColorName("");
    setNewColorValue("#3B82F6");
    setEditingColor(null);
  };

  const resetSizeForm = () => {
    setNewSizeName("");
    setNewSizeValue("");
    setNewSizeUnit("px");
    setEditingSize(null);
  };

  const resetFontForm = () => {
    setNewFontName("");
    setNewFontValue("");
    setEditingFont(null);
  };

  const resetIconForm = () => {
    setNewIconName("");
    setNewIconValue("");
    setEditingIcon(null);
    setIconNameTouched(false);
    setIconSearch("");
  };

  const handleSaveColor = () => {
    if (!craftspaceId || !newColorName.trim()) return;

    if (editingColor) {
      updateCraftspaceColorVariable(craftspaceId, editingColor.id, {
        name: newColorName,
        value: newColorValue,
      });
    } else {
      addCraftspaceColorVariable(craftspaceId, {
        name: newColorName,
        value: newColorValue,
      });
    }

    resetColorForm();
    setColorDialogOpen(false);
  };

  const handleSaveSize = () => {
    if (!craftspaceId || !newSizeName.trim() || !newSizeValue.trim()) return;

    const value = `${newSizeValue}${newSizeUnit}`;

    if (editingSize) {
      updateCraftspaceSizeVariable(craftspaceId, editingSize.id, {
        name: newSizeName,
        value: value,
      });
    } else {
      addCraftspaceSizeVariable(craftspaceId, {
        name: newSizeName,
        value: value,
      });
    }

    resetSizeForm();
    setSizeDialogOpen(false);
  };

  const handleSaveFont = () => {
    if (!craftspaceId || !newFontName.trim() || !newFontValue.trim()) return;

    if (editingFont) {
      updateCraftspaceFontFamilyVariable(craftspaceId, editingFont.id, {
        name: newFontName,
        value: newFontValue,
      });
    } else {
      addCraftspaceFontFamilyVariable(craftspaceId, {
        name: newFontName,
        value: newFontValue,
      });
    }

    resetFontForm();
    setFontDialogOpen(false);
  };

  const handleSaveIcon = () => {
    if (!craftspaceId || !newIconName.trim() || !newIconValue.trim()) return;

    if (editingIcon) {
      updateCraftspaceIconVariable(craftspaceId, editingIcon.id, {
        name: newIconName,
        value: newIconValue,
      });
    } else {
      addCraftspaceIconVariable(craftspaceId, {
        name: newIconName,
        value: newIconValue,
      });
    }

    resetIconForm();
    setIconDialogOpen(false);
  };

  const handleEditColor = (color: ColorVariable) => {
    setEditingColor(color);
    setNewColorName(color.name);
    setNewColorValue(color.value);
    setColorDialogOpen(true);
  };

  const handleEditSize = (size: SizeVariable) => {
    setEditingSize(size);
    setNewSizeName(size.name);

    // Extract number and unit from value
    const match = size.value.match(/^(\d+(?:\.\d+)?)(px|%|rem|em|vw|vh)$/);
    if (match) {
      setNewSizeValue(match[1]);
      setNewSizeUnit(match[2]);
    } else {
      setNewSizeValue(size.value);
      setNewSizeUnit("px");
    }

    setSizeDialogOpen(true);
  };

  const handleEditFont = (font: FontFamilyVariable) => {
    setEditingFont(font);
    setNewFontName(font.name);
    setNewFontValue(font.value);
    setFontDialogOpen(true);
  };

  const handleEditIcon = (icon: IconVariable) => {
    setEditingIcon(icon);
    setNewIconName(icon.name);
    setNewIconValue(icon.value);
    setIconNameTouched(true);
    setIconDialogOpen(true);
  };

  const preferredIconOrder = [
    "Info",
    "ChevronUp",
    "ChevronDown",
    "ChevronLeft",
    "ChevronRight",
    "Plus",
    "Play",
    "CirclePlay",
    "Heart",
    "Star",
    "Volume1",
    "Volume2",
    "VolumeOff",
    "VolumeX",
    "ShoppingCart",
    "Map",
    "MapPin",
    "MoveUpRight",
    "SquareArrowOutUpRight",
  ];
  const lucideIconEntries = useMemo(() => {
    const entries = Object.entries(icons);
    const entryMap = new Map(entries);
    const preferredSet = new Set(preferredIconOrder);
    const orderedPreferred = preferredIconOrder
      .filter((name) => entryMap.has(name))
      .map((name) => [name, entryMap.get(name)] as [string, (typeof icons)[keyof typeof icons]]);
    const remaining = entries
      .filter(([name]) => !preferredSet.has(name))
      .sort(([a], [b]) => a.localeCompare(b));
    return [...orderedPreferred, ...remaining];
  }, []);

  const filteredLucideIcons = useMemo(() => {
    const query = iconSearch.trim().toLowerCase();
    if (!query) return lucideIconEntries;
    return lucideIconEntries.filter(([name]) => name.toLowerCase().includes(query));
  }, [iconSearch, lucideIconEntries]);

  const toKebabCase = (value: string) =>
    value
      .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
      .replace(/[_\\s]+/g, "-")
      .toLowerCase();

  const renderIconPreview = (value: string) => {
    const trimmed = value.trim();
    if (trimmed.startsWith("lucide:")) {
      const iconName = trimmed.slice("lucide:".length);
      const IconComponent = (icons as Record<string, React.ComponentType<{ size?: number; className?: string }>>)[iconName];
      if (IconComponent) {
        return <IconComponent size={16} className="text-gray-200" />;
      }
    }
    const isUrl = /^(https?:)?\/\//i.test(trimmed);
    const isDataImage = /^data:image\//i.test(trimmed);
    if (!isUrl && !isDataImage) return null;
    return (
      <img
        src={trimmed}
        alt=""
        className="w-5 h-5 rounded bg-gray-800/60 object-contain"
      />
    );
  };

  // Helper function to render color swatch with transparency pattern
  const renderColorSwatch = (
    color: string,
    className: string = "w-6 h-6 rounded border border-gray-400",
  ) => {
    // Check for transparent colors (including rgba with alpha 0)
    const isTransparent =
      color.toLowerCase() === "transparent" ||
      color === "rgba(0,0,0,0)" ||
      color === "rgba(0, 0, 0, 0)" ||
      color === "hsla(0,0%,0%,0)" ||
      color === "hsla(0, 0%, 0%, 0)";

    if (isTransparent) {
      return (
        <div
          className={className}
          style={{
            backgroundImage: `
              linear-gradient(45deg, #e5e5e5 25%, transparent 25%),
              linear-gradient(-45deg, #e5e5e5 25%, transparent 25%),
              linear-gradient(45deg, transparent 75%, #e5e5e5 75%),
              linear-gradient(-45deg, transparent 75%, #e5e5e5 75%)
            `,
            backgroundSize: "6px 6px",
            backgroundPosition: "0 0, 0 3px, 3px -3px, -3px 0px",
            backgroundColor: "#ffffff",
          }}
        />
      );
    }

    return <div className={className} style={{ backgroundColor: color }} />;
  };

  if (!craftspaceId) {
    return <div className="text-white">No craftspace selected</div>;
  }

  const colorCard = (
    <Card key="colors" className="bg-[#1a2332] border-gray-700/50">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-white flex items-center space-x-2 text-base font-semibold">
            <Palette className="h-4 w-4 text-gray-400" />
            <span>Color Variables</span>
          </CardTitle>
          <Dialog open={colorDialogOpen} onOpenChange={setColorDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={resetColorForm}
                size="sm"
                className="bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white border-0 text-[10px] font-semibold tracking-widest uppercase h-7 px-3"
              >
                <Plus className="h-3 w-3 mr-1" />
                Add Color
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#1a2332] border-gray-700">
              <DialogHeader>
                <DialogTitle className="text-white">
                  {editingColor ? "Edit Color Variable" : "Add Color Variable"}
                </DialogTitle>
              </DialogHeader>
              <div className="relative space-y-4">
                <div>
                  <Label htmlFor="color-name" className="text-gray-300 text-xs uppercase tracking-wide">
                    Variable Name
                  </Label>
                  <Input
                    id="color-name"
                    value={newColorName}
                    onChange={(e) => setNewColorName(e.target.value)}
                    placeholder="e.g., primary-color"
                    className="bg-gray-800 border-gray-600 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-300 text-xs uppercase tracking-wide">Color Value</Label>
                  <div className="space-y-3 mt-1">
                    <HexColorPicker color={newColorValue} onChange={setNewColorValue} />
                    <Input
                      value={newColorValue}
                      onChange={(e) => setNewColorValue(e.target.value)}
                      placeholder="#3B82F6"
                      className="bg-gray-800 border-gray-600 text-white"
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-2">
                  <Button
                    variant="outline"
                    onClick={() => setColorDialogOpen(false)}
                    className="border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white text-xs uppercase tracking-wide"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveColor}
                    className="bg-gray-700 hover:bg-gray-600 text-gray-200 hover:text-white border-0 text-xs uppercase tracking-wide"
                  >
                    {editingColor ? "Update" : "Add"} Color
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {variables.colors.length === 0 ? (
          <p className="text-gray-500 text-sm">No color variables defined yet.</p>
        ) : (
          <div className="space-y-1">
            {variables.colors.map((color) => (
              <div
                key={color.id}
                className="flex items-center justify-between px-3 py-2.5 bg-gray-800/50 hover:bg-gray-800 rounded-md transition-colors"
              >
                <div className="flex items-center space-x-3">
                  {renderColorSwatch(color.value, "w-5 h-5 rounded border border-white/10 flex-shrink-0")}
                  <div>
                    <div className="text-white text-sm font-medium">{color.name}</div>
                    <div className="text-gray-500 text-xs">{color.value}</div>
                  </div>
                </div>
                <div className="flex space-x-0.5">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleEditColor(color)}
                    className="text-gray-500 hover:text-white hover:bg-gray-700 h-7 w-7 p-0"
                  >
                    <Edit2 className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => deleteCraftspaceColorVariable(craftspaceId, color.id)}
                    className="text-gray-500 hover:text-red-400 hover:bg-gray-700 h-7 w-7 p-0"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );

  const sizeCard = (
    <Card key="sizes" className="bg-[#1a2332] border-gray-700/50">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-white flex items-center space-x-2 text-base font-semibold">
            <Ruler className="h-4 w-4 text-gray-400" />
            <span>Size Variables</span>
          </CardTitle>
          <Dialog open={sizeDialogOpen} onOpenChange={setSizeDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={resetSizeForm}
                size="sm"
                className="bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white border-0 text-[10px] font-semibold tracking-widest uppercase h-7 px-3"
              >
                <Plus className="h-3 w-3 mr-1" />
                Add Size
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#1a2332] border-gray-700">
              <DialogHeader>
                <DialogTitle className="text-white">
                  {editingSize ? "Edit Size Variable" : "Add Size Variable"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="size-name" className="text-gray-300 text-xs uppercase tracking-wide">
                    Variable Name
                  </Label>
                  <Input
                    id="size-name"
                    value={newSizeName}
                    onChange={(e) => setNewSizeName(e.target.value)}
                    placeholder="e.g., header-height"
                    className="bg-gray-800 border-gray-600 text-white mt-1"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label htmlFor="size-value" className="text-gray-300 text-xs uppercase tracking-wide">
                      Value
                    </Label>
                    <Input
                      id="size-value"
                      value={newSizeValue}
                      onChange={(e) => setNewSizeValue(e.target.value)}
                      placeholder="20"
                      className="bg-gray-800 border-gray-600 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="size-unit" className="text-gray-300 text-xs uppercase tracking-wide">
                      Unit
                    </Label>
                    <Select value={newSizeUnit} onValueChange={setNewSizeUnit}>
                      <SelectTrigger className="bg-gray-800 border-gray-600 text-white mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700">
                        {["px", "%", "rem", "em", "vw", "vh"].map((u) => (
                          <SelectItem key={u} value={u} className="text-white">{u}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex justify-end space-x-2">
                  <Button
                    variant="outline"
                    onClick={() => setSizeDialogOpen(false)}
                    className="border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white text-xs uppercase tracking-wide"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveSize}
                    className="bg-gray-700 hover:bg-gray-600 text-gray-200 hover:text-white border-0 text-xs uppercase tracking-wide"
                  >
                    {editingSize ? "Update" : "Add"} Size
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {variables.sizes.length === 0 ? (
          <p className="text-gray-500 text-sm">No size variables defined yet.</p>
        ) : (
          <div className="space-y-1">
            {variables.sizes.map((size) => (
              <div
                key={size.id}
                className="flex items-center justify-between px-3 py-2.5 bg-gray-800/50 hover:bg-gray-800 rounded-md transition-colors"
              >
                <div>
                  <div className="text-white text-sm font-medium">{size.name}</div>
                  <div className="text-gray-500 text-xs">{size.value}</div>
                </div>
                <div className="flex space-x-0.5">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleEditSize(size)}
                    className="text-gray-500 hover:text-white hover:bg-gray-700 h-7 w-7 p-0"
                  >
                    <Edit2 className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => deleteCraftspaceSizeVariable(craftspaceId, size.id)}
                    className="text-gray-500 hover:text-red-400 hover:bg-gray-700 h-7 w-7 p-0"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );

  const fontCard = (
    <Card key="fonts" className="bg-[#1a2332] border-gray-700/50">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-white flex items-center space-x-2 text-base font-semibold">
            <Type className="h-4 w-4 text-gray-400" />
            <span>Font Family Variables</span>
          </CardTitle>
          <Dialog open={fontDialogOpen} onOpenChange={setFontDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={resetFontForm}
                size="sm"
                className="bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white border-0 text-[10px] font-semibold tracking-widest uppercase h-7 px-3"
              >
                <Plus className="h-3 w-3 mr-1" />
                Add Font
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#1a2332] border-gray-700">
              <DialogHeader>
                <DialogTitle className="text-white">
                  {editingFont ? "Edit Font Family Variable" : "Add Font Family Variable"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="font-name" className="text-gray-300 text-xs uppercase tracking-wide">
                    Variable Name
                  </Label>
                  <Input
                    id="font-name"
                    value={newFontName}
                    onChange={(e) => setNewFontName(e.target.value)}
                    placeholder="e.g., heading-font"
                    className="bg-gray-800 border-gray-600 text-white mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="font-value" className="text-gray-300 text-xs uppercase tracking-wide">
                    Font Family
                  </Label>
                  <Input
                    id="font-value"
                    value={newFontValue}
                    onChange={(e) => setNewFontValue(e.target.value)}
                    placeholder="e.g., 'Inter', sans-serif"
                    className="bg-gray-800 border-gray-600 text-white mt-1"
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <Button
                    variant="outline"
                    onClick={() => setFontDialogOpen(false)}
                    className="border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white text-xs uppercase tracking-wide"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveFont}
                    className="bg-gray-700 hover:bg-gray-600 text-gray-200 hover:text-white border-0 text-xs uppercase tracking-wide"
                  >
                    {editingFont ? "Update" : "Add"} Font
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {variables.fontFamilies.length === 0 ? (
          <p className="text-gray-500 text-sm">No font family variables defined yet.</p>
        ) : (
          <div className="space-y-1">
            {variables.fontFamilies.map((font) => (
              <div
                key={font.id}
                className="flex items-center justify-between px-3 py-2.5 bg-gray-800/50 hover:bg-gray-800 rounded-md transition-colors"
              >
                <div>
                  <div className="text-white text-sm font-medium">{font.name}</div>
                  <div className="text-gray-500 text-xs" style={{ fontFamily: font.value }}>
                    {font.value}
                  </div>
                </div>
                <div className="flex space-x-0.5">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleEditFont(font)}
                    className="text-gray-500 hover:text-white hover:bg-gray-700 h-7 w-7 p-0"
                  >
                    <Edit2 className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => deleteCraftspaceFontFamilyVariable(craftspaceId, font.id)}
                    className="text-gray-500 hover:text-red-400 hover:bg-gray-700 h-7 w-7 p-0"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );

  const iconCard = (
    <Card key="icons" className="bg-[#1a2332] border-gray-700/50">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-white flex items-center space-x-2 text-base font-semibold">
            <Image className="h-4 w-4 text-gray-400" />
            <span>Icon Variables</span>
          </CardTitle>
          <Dialog open={iconDialogOpen} onOpenChange={setIconDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={resetIconForm}
                size="sm"
                className="bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white border-0 text-[10px] font-semibold tracking-widest uppercase h-7 px-3"
              >
                <Plus className="h-3 w-3 mr-1" />
                Add Icon
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#1a2332] border-gray-700">
              <DialogHeader>
                <DialogTitle className="text-white">
                  {editingIcon ? "Edit Icon Variable" : "Add Icon Variable"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-gray-300 text-xs uppercase tracking-wide">
                      Lucide Icons
                    </Label>
                    <span className="text-xs text-gray-500">
                      {filteredLucideIcons.length} icons
                    </span>
                  </div>
                  <Input
                    value={iconSearch}
                    onChange={(e) => setIconSearch(e.target.value)}
                    placeholder="Search icons..."
                    className="bg-gray-800 border-gray-600 text-white"
                  />
                  <div className="grid grid-cols-6 gap-2 max-h-48 overflow-y-auto rounded border border-gray-700 p-2 bg-gray-900/40">
                    {filteredLucideIcons.map(([name, IconComponent]) => (
                      <button
                        key={name}
                        type="button"
                        onClick={() => {
                          setNewIconValue(`lucide:${name}`);
                          if (!iconNameTouched) {
                            setNewIconName(toKebabCase(name));
                          }
                        }}
                        className="flex items-center justify-center h-9 w-9 rounded bg-gray-700 hover:bg-gray-600 text-gray-200"
                        title={name}
                        aria-label={`Use ${name} icon`}
                      >
                        <IconComponent size={16} />
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label htmlFor="icon-name" className="text-gray-300 text-xs uppercase tracking-wide">
                    Variable Name
                  </Label>
                  <Input
                    id="icon-name"
                    value={newIconName}
                    onChange={(e) => {
                      setNewIconName(e.target.value);
                      setIconNameTouched(true);
                    }}
                    placeholder="e.g., play-icon"
                    className="bg-gray-800 border-gray-600 text-white mt-1"
                  />
                </div>
                <div className="flex items-center justify-between">
                  {newIconValue.trim() ? (
                    <div className="h-9 w-9 flex items-center justify-center rounded bg-gray-700/40">
                      <div className="scale-150">{renderIconPreview(newIconValue)}</div>
                    </div>
                  ) : (
                    <div />
                  )}
                  <div className="flex justify-end space-x-2">
                  <Button
                    variant="outline"
                    onClick={() => setIconDialogOpen(false)}
                    className="border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white text-xs uppercase tracking-wide"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveIcon}
                    className="bg-gray-700 hover:bg-gray-600 text-gray-200 hover:text-white border-0 text-xs uppercase tracking-wide"
                  >
                    {editingIcon ? "Update" : "Add"} Icon
                  </Button>
                  </div>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {variables.icons.length === 0 ? (
          <p className="text-gray-500 text-sm">No icon variables defined yet.</p>
        ) : (
          <div className="space-y-1">
            {variables.icons.map((icon) => (
              <div
                key={icon.id}
                className="flex items-center justify-between px-3 py-2.5 bg-gray-800/50 hover:bg-gray-800 rounded-md transition-colors"
              >
                <div className="flex items-center space-x-3 min-w-0">
                  {renderIconPreview(icon.value)}
                  <div className="min-w-0">
                    <div className="text-white text-sm font-medium">{icon.name}</div>
                    <div className="text-gray-500 text-xs truncate max-w-[280px]">{icon.value}</div>
                  </div>
                </div>
                <div className="flex space-x-0.5">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleEditIcon(icon)}
                    className="text-gray-500 hover:text-white hover:bg-gray-700 h-7 w-7 p-0"
                  >
                    <Edit2 className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => deleteCraftspaceIconVariable(craftspaceId, icon.id)}
                    className="text-gray-500 hover:text-red-400 hover:bg-gray-700 h-7 w-7 p-0"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );

  const variableCards = (
    <>
      {(!section || section === "colors") && colorCard}
      {(!section || section === "sizes") && sizeCard}
      {(!section || section === "fonts") && fontCard}
      {(!section || section === "icons") && iconCard}
    </>
  );

  if (embedded) {
    const embeddedTitle =
      section === "colors"
        ? "Craftspace Colors"
        : section === "sizes"
          ? "Craftspace Sizes"
          : section === "icons"
            ? "Craftspace Icons"
            : section === "fonts"
              ? "Craftspace Font Family"
              : "Craftspace Variables";

    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <h2 className="text-xl font-semibold text-white">{embeddedTitle}</h2>
          <p className="text-sm text-gray-400">
            Craftspace variables can be used across all projects for consistent styling.
          </p>
        </div>
        {variableCards}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                onClick={() => navigate("/dashboard")}
                className="text-gray-300 hover:text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-white">Craftspace Settings</h1>
                <p className="text-gray-300">Manage your craftspace and team members</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Settings className="w-5 h-5 text-gray-400" />
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <div className="bg-gray-800 border-b border-gray-700">
        <div className="container mx-auto px-6">
          <nav className="flex space-x-8">
            <Button
              variant="ghost"
              onClick={() => navigate(`/craftspace/${craftspaceId}/settings`)}
              className="text-gray-300 hover:text-white"
            >
              <Settings className="w-4 h-4 mr-2" />
              General
            </Button>
            <Button
              variant="ghost"
              className="text-gray-300 hover:text-white border-b-2 border-blue-600"
            >
              <Palette className="w-4 h-4 mr-2" />
              Variables
            </Button>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-8">
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="flex items-center space-x-3">
            <VariablesIcon size={20} className="text-white" />
            <h2 className="text-xl font-semibold text-white">Craftspace Variables</h2>
          </div>

          <p className="text-sm text-gray-400">
            Craftspace variables can be used across all projects for consistent styling.
          </p>

          <p className="text-gray-300 text-sm">
            Define variables that will be available across all projects in this craftspace.
            Project-specific variables will override craftspace variables with the same name.
          </p>

          {variableCards}
        </div>
      </div>
    </div>
  );
};

export default CraftspaceVariables;
